import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { QRCodeSVG } from 'qrcode.react';
import { Calendar, CheckCircle, CreditCard, UserRound } from 'lucide-react';
import { db } from '../../services/firebase';
import { CHECK_IN_ACTION, getQrLink } from '../../utils/checkInOut';
import { formatVoucherDate, formatVoucherDateTime } from '../../utils/voucher';
import LoadingState from '../shared/LoadingState';
import './VoucherPage.css';

const VoucherPage = () => {
  const { token } = useParams();
  const [voucher, setVoucher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const loadVoucher = async () => {
      if (!token) {
        setMessage('Voucher inválido.');
        setLoading(false);
        return;
      }

      try {
        const snap = await getDoc(doc(db, 'publicVouchers', token));
        if (!snap.exists()) {
          setMessage('Voucher não encontrado.');
          return;
        }
        setVoucher({ id: snap.id, ...snap.data() });
      } catch (error) {
        console.error('Erro ao carregar voucher:', error);
        setMessage('Não foi possível carregar este voucher.');
      } finally {
        setLoading(false);
      }
    };

    loadVoucher();
  }, [token]);

  if (loading) {
    return <LoadingState variant="screen" title="Carregando voucher" subtitle="Preparando dados da reserva" />;
  }

  if (message) {
    return (
      <main className="voucher-page">
        <section className="voucher-card voucher-card--message">
          <h1>{message}</h1>
          <p>Confira o link recebido ou solicite um novo voucher ao atendimento.</p>
        </section>
      </main>
    );
  }

  const checkInLink = getQrLink({
    companyId: voucher.companyId,
    reservationId: voucher.reservationId,
    action: CHECK_IN_ACTION,
    token,
    origin: window.location.origin
  });

  const heroStyle = {
    background: `linear-gradient(135deg, ${voucher.companyPrimaryColor || '#2563eb'} 0%, ${voucher.companySecondaryColor || '#0f766e'} 100%)`
  };

  return (
    <main className="voucher-page">
      <section className="voucher-hero" style={heroStyle}>
        <div className="voucher-brand">
          <div className="voucher-logo">
            {voucher.companyLogoUrl ? <img src={voucher.companyLogoUrl} alt={voucher.companyName} /> : <span>{voucher.companyName?.charAt(0) || 'V'}</span>}
          </div>
          <div>
            <p>Voucher de reserva</p>
            <h1>{voucher.companyName || 'Empresa'}</h1>
          </div>
        </div>
        <span className="voucher-status">{voucher.status || 'pending'}</span>
      </section>

      <section className="voucher-card">
        <div className="voucher-main">
          <div>
            <p className="voucher-label">Cliente</p>
            <h2>{voucher.clientName || '-'}</h2>
            <p className="voucher-service">{voucher.serviceName || '-'}</p>
            {voucher.roomName && <p className="voucher-room">{voucher.roomName}</p>}
          </div>
          <div className="voucher-qr">
            <QRCodeSVG value={checkInLink} size={180} includeMargin />
            <span>QR validador de check-in</span>
          </div>
        </div>

        <div className="voucher-details">
          <div>
            <Calendar size={18} />
            <span>{voucher.checkInDate && voucher.checkOutDate ? `${formatVoucherDate(voucher.checkInDate)} ate ${formatVoucherDate(voucher.checkOutDate)}` : formatVoucherDateTime(voucher.date)}</span>
          </div>
          <div>
            <UserRound size={18} />
            <span>{voucher.participants || 1} participante(s)</span>
          </div>
          <div>
            <CreditCard size={18} />
            <span>{voucher.paymentStatus || 'unpaid'}</span>
          </div>
          <div>
            <CheckCircle size={18} />
            <span>{voucher.checkInStatus === 'checked_in' ? 'Check-in realizado' : 'Aguardando check-in'}</span>
          </div>
        </div>
      </section>
    </main>
  );
};

export default VoucherPage;
